import React from "react";
import { Container, Row, Col} from "reactstrap";
import Chart from "react-apexcharts";
import CommonTabHeader from "Components/Common/CommonTabHeader";
import styles from "../css/sessionDashboard.module.scss";
import skeletonStyles from "assets/scss/common/viewSkeleton.module.scss";
import { useGlobalStudy } from "context/StudyContext";
import { useSessionDashboardService } from "../api/useSession";

const SessionDashboard: React.FC = () => {
  document.title = "Session Dashboard | ZENTIXS VISTA"
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const studyId = globalStudy?.studyid ? Number(globalStudy.studyid) : null;

  const { SessionDashboardQuery } = useSessionDashboardService({
    studyid: studyId,
    periodid: null,
  });

  const dashboardData = SessionDashboardQuery?.data?.data || {};

  /* ----------------------------------------------------
     Donut Chart Options Generator
     ---------------------------------------------------- */
  const createDonutOptions = (
    labels: string[],
    colors: string[],
    totalLabel: string,
    totalValue: number
  ): ApexCharts.ApexOptions => ({
    chart: {
      type: "donut",
    },
    grid: {
      padding: {
        top: 5,
        bottom: 5,
        left: -10,
        right: -10,
      },
    },
    labels,
    colors,
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      theme: "light",
      fillSeriesColor: false,
      style: {
        fontSize: "12px",
        fontFamily: "inherit",
      },
      y: {
        formatter: (val) => `${val}`,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "#64748b",
              offsetY: -6,
            },
            value: {
              show: true,
              fontSize: "1.1rem",
              fontWeight: 700,
              color: "#0f172a",
              offsetY: 4,
            },
            total: {
              show: true,
              showAlways: true,
              label: totalLabel,
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "#64748b",
              formatter: () => String(totalValue),
            },
          },
        },
      },
    },
  });

  // Extract API data
  const summary = dashboardData?.summary || {};
  const sessionState = dashboardData?.session_state || {};
  const sampleCollection = dashboardData?.sample_collection || {};
  const sampleSeparation = dashboardData?.sample_separation || {};
  const protocolDeviation = dashboardData?.protocol_deviation || {};
  const periodTimepointList: any[] = Array.isArray(dashboardData?.period_timepoint)
    ? dashboardData.period_timepoint
    : [];

  // 5 Summary cards values
  const totalSessions = summary.total_sessions ?? 0;
  const totalSubjects = summary.total_subjects ?? 0;
  const completedCollection = sampleCollection.completed ?? 0;
  const completedSeparation = sampleSeparation.completed ?? 0;
  const protocolDeviationLate = protocolDeviation.late ?? 0;

  // ── Donut 1: Session State ──────────────────────────────────────────
  const sessionStateCompleted = sessionState.completed ?? 0;
  const sessionStatePaused = sessionState.paused ?? 0;
  const sessionStateActive = sessionState.active ?? 0;
  const sessionStateTotal = sessionState.total ?? (sessionStateCompleted + sessionStatePaused + sessionStateActive);

  const sessionStateOptions = createDonutOptions(
    ["Completed", "Paused", "Active"],
    ["#2563eb", "#f59e0b", "#10b981"],
    "Sessions",
    sessionStateTotal
  );
  const sessionStateSeries = [sessionStateCompleted, sessionStatePaused, sessionStateActive];

  // ── Donut 2: Sample Collection ─────────────────────────────────────
  const collectionCompleted = sampleCollection.completed ?? 0;
  const collectionSkipped = sampleCollection.skipped ?? 0;
  const collectionPending = sampleCollection.pending ?? 0;
  const collectionTotal = sampleCollection.total ?? (collectionCompleted + collectionSkipped + collectionPending);

  const sampleCollectionOptions = createDonutOptions(
    ["Completed", "Skipped", "Pending"],
    ["#10b981", "#ef4444", "#94a3b8"],
    "Samples",
    collectionTotal
  );
  const sampleCollectionSeries = [collectionCompleted, collectionSkipped, collectionPending];

  // ── Donut 3: Sample Separation ─────────────────────────────────────
  const separationCompleted = sampleSeparation.completed ?? 0;
  const separationPending = sampleSeparation.pending ?? 0;
  const separationTotal = sampleSeparation.total ?? (separationCompleted + separationPending);

  const sampleSeparationOptions = createDonutOptions(
    ["Completed", "Pending"],
    ["#0f766e", "#94a3b8"],
    "Aliquots",
    separationTotal
  );
  const sampleSeparationSeries = [separationCompleted, separationPending];

  // ── Bar Chart 1: Period & Timepoint Bar Options (3 Stacked Data Segments per Period) ────
  const periodCategories = periodTimepointList.map((pt: any, idx: number) => {
    const pNo = pt.period_number;
    return `Period ${pNo}`;
  });

  const periodCompletedTp = periodTimepointList.map((pt: any) => pt.completed_timepoints ?? 0);
  const periodSessionPendingTp = periodTimepointList.map((pt: any) => {
    const total = pt.total_timepoints ?? 0;
    const completed = pt.completed_timepoints ?? 0;
    return Math.max(0, total - completed);
  });
  const periodUncreatedTp = periodTimepointList.map((pt: any) => {
    const studyTotal = pt.study_total_timepoints ?? pt.total_timepoints ?? 0;
    const sessionTotal = pt.total_timepoints ?? 0;
    return Math.max(0, studyTotal - sessionTotal);
  });

  const studyTotalTpList = periodTimepointList.map((pt: any) => pt.study_total_timepoints ?? pt.total_timepoints ?? 0);
  const maxPeriodTp = Math.max(...studyTotalTpList, 6);

  const dynamicColumnWidth =
    periodCategories.length === 1
      ? "15%"
      : periodCategories.length === 2
      ? "25%"
      : "32%";

  const periodBarOptions: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: dynamicColumnWidth,
        borderRadius: 0,
      },
    },
    colors: ["#10b981", "#2563eb", "#cbd5e1"], // Completed: Green, Session Pending: Blue, Uncreated: Light Slate
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      fontSize: "11px",
      fontWeight: 600,
      labels: {
        colors: "#475569",
      },
      markers: {
        size: 4,
      },
    },
    xaxis: {
      categories: periodCategories.length > 0 ? periodCategories : ["No Data"],
      axisBorder: {
        show: true,
        color: "#e2e8f0",
      },
      axisTicks: {
        show: true,
        color: "#e2e8f0",
      },
      labels: {
        style: {
          colors: "#475569",
          fontSize: "12px",
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxPeriodTp * 1.2),
      tickAmount: 4,
      labels: {
        style: {
          colors: "#64748b",
          fontSize: "12px",
        },
        formatter: (val) => `${Math.round(val)}`,
      },
    },
    grid: {
      show: true,
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
      padding: {
        left: 15,
        right: 15,
        top: 10,
        bottom: 0,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: "light",
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const periodName = w.globals.labels[dataPointIndex] || "";
        const completed = series[0]?.[dataPointIndex] ?? 0;
        const inProgress = series[1]?.[dataPointIndex] ?? 0;
        const remaining = series[2]?.[dataPointIndex] ?? 0;
        const sessionTotal = completed + inProgress;
        const studyTotal = sessionTotal + remaining;

        return `
          <div style="padding: 8px 12px; font-size: 12px; font-family: inherit;">
            <div style="font-weight: 700; margin-bottom: 4px; color: #0f172a;">${periodName}</div>
            <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 3px;">
              <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #10b981;"></span>
              <span>Completed Timepoints: <b>${completed}</b></span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 3px;">
              <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #2563eb;"></span>
              <span>In Progress Timepoints: <b>${inProgress}</b></span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 3px;">
              <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #94a3b8;"></span>
              <span>Remaining Timepoints: <b>${remaining}</b></span>
            </div>
            <div style="border-top: 1px solid #e2e8f0; margin-top: 4px; padding-top: 4px; font-weight: 600; color: #334155;">
              Total Timepoints: <b>${studyTotal}</b>
            </div>
          </div>
        `;
      },
    },
  };

  const periodBarSeries = [
    {
      name: "Completed",
      data: periodCompletedTp.length > 0 ? periodCompletedTp : [0],
    },
    {
      name: "In Progress",
      data: periodSessionPendingTp.length > 0 ? periodSessionPendingTp : [0],
    },
    {
      name: "Remaining",
      data: periodUncreatedTp.length > 0 ? periodUncreatedTp : [0],
    },
  ];

  // ── Bar Chart 2: Collection Timing (protocol_deviation) ─────────────
  const earlyVal = protocolDeviation.early ?? 0;
  const onTimeVal = protocolDeviation.ontime ?? 0;
  const lateVal = protocolDeviation.late ?? 0;
  const notCollectedVal = protocolDeviation.not_collected ?? 0;

  const timingCategories = ["Early", "On Time", "Late", "Not Collected"];
  const timingSeriesData = [earlyVal, onTimeVal, lateVal, notCollectedVal];
  const maxTimingValue = Math.max(...timingSeriesData, 10);

  const timingBarOptions: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "35%",
        borderRadius: 4,
        distributed: true,
      },
    },
    colors: ["#f59e0b", "#10b981", "#ef4444", "#94a3b8"], // Early: Amber, On Time: Green, Late: Red, Not Collected: Gray
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    xaxis: {
      categories: timingCategories,
      axisBorder: {
        show: true,
        color: "#e2e8f0",
      },
      axisTicks: {
        show: true,
        color: "#e2e8f0",
      },
      labels: {
        style: {
          colors: "#475569",
          fontSize: "12px",
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxTimingValue * 1.2),
      labels: {
        style: {
          colors: "#64748b",
          fontSize: "12px",
        },
      },
    },
    grid: {
      show: true,
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
      padding: {
        left: 15,
        right: 15,
        top: 10,
        bottom: 0,
      },
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: (val) => `${val} Collections`,
      },
    },
  };

  const timingBarSeries = [
    {
      name: "Collections",
      data: timingSeriesData,
    },
  ];

  const renderEmptyState = (iconClass: string, title: string, subtitle: string) => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        padding: "24px 16px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          backgroundColor: "#f8fafc",
          border: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "12px",
        }}
      >
        <i className={iconClass} style={{ fontSize: "22px", color: "#94a3b8" }} />
      </div>
      <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a", marginBottom: "4px" }}>
        {title}
      </div>
      <div style={{ fontSize: "12px", color: "#64748b", maxWidth: "260px", lineHeight: 1.4 }}>
        {subtitle}
      </div>
    </div>
  );

  const renderSkeleton = () => (
    <>
      {/* ── Top 5 Stats Cards Grid Skeleton ─────────────────────────── */}
      <div className={styles.statsGrid}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i}>
            <div className={styles.statItemCard}>
              <div className={styles.cardHeaderRow}>
                <div className={skeletonStyles.skeleton} style={{ height: "24px", width: "40px" }} />
                <div className={`${skeletonStyles.skeleton} ${skeletonStyles.skeletonIcon}`} style={{ width: "2.25rem", height: "2.25rem", borderRadius: "50%" }} />
              </div>
              <div className={styles.cardBodyContent}>
                <div className={skeletonStyles.skeleton} style={{ height: "12px", width: "90px", marginBottom: "6px" }} />
                <div className={skeletonStyles.skeleton} style={{ height: "10px", width: "130px" }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Middle Row: 3 Donut Chart Cards Skeleton ────────────────────── */}
      <Row className="g-3 mb-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Col xs={12} xl={4} key={i}>
            <div className={styles.premiumCard}>
              <div className={styles.cardHeader}>
                <div className={skeletonStyles.skeleton} style={{ height: "14px", width: "120px" }} />
              </div>
              <div className={styles.cardBody} style={{ minHeight: "200px", display: "flex", alignItems: "center", padding: "20px" }}>
                <div className={styles.donutChartWrapper}>
                  <div className={styles.chartContainer}>
                    <div className={skeletonStyles.skeleton} style={{ height: "115px", width: "115px", borderRadius: "50%" }} />
                  </div>
                  <div className={styles.donutLegend}>
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div key={j} className={styles.legendRow} style={{ borderBottom: "none" }}>
                        <div className={skeletonStyles.skeleton} style={{ height: "10px", width: "80px" }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ── Bottom Row: 2 Graph Cards Skeleton ──────────────────────────── */}
      <Row className="g-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <Col xs={12} xl={6} key={i}>
            <div className={styles.premiumCard}>
              <div className={styles.cardHeader}>
                <div className={skeletonStyles.skeleton} style={{ height: "14px", width: "140px" }} />
              </div>
              <div className={styles.cardBody} style={{ minHeight: "220px", display: "flex", alignItems: "flex-end", justifyContent: "space-around", padding: "20px" }}>
                {Array.from({ length: 4 }).map((_, j) => (
                  <div
                    key={j}
                    className={skeletonStyles.skeleton}
                    style={{ height: `${30 + (j % 3) * 25}%`, width: "35px", borderRadius: "4px" }}
                  />
                ))}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );

  return (
    <div style={{ overflowX: "hidden", minHeight: "100vh", backgroundColor: "#f4f7fa" }}>
      {/* ── Page Header Bar (Full Width Top Navigation & Title) ──────── */}
      <div style={{ padding: "0 12px", backgroundColor: "#fff" }}>
        <CommonTabHeader title="Session Dashboard" />
      </div>

      <div className={styles.dashboardWrapper}>
        <Container fluid className="p-0">
          {SessionDashboardQuery.isFetching ? (
            renderSkeleton()
          ) : (
            <>
              {/* ── Top 5 Stats Cards Row (Single Horizontal Row) ───────────── */}
              <div className={styles.statsGrid}>
                {/* Card 1 */}
                <div>
                  <div className={styles.statItemCard}>
                    <div className={styles.cardHeaderRow}>
                      <div className={styles.value}>{totalSessions}</div>
                      <div className={`${styles.iconBadge} ${styles.blue}`}>
                        <i className="ri-layout-grid-line" />
                      </div>
                    </div>
                    <div className={styles.cardBodyContent}>
                      <div className={styles.label}>Total Session</div>
                      <div className={styles.subtext}>Active &amp; Completed</div>
                    </div>
                  </div>
                </div>

                {/* Card 2 */}
                <div>
                  <div className={styles.statItemCard}>
                    <div className={styles.cardHeaderRow}>
                      <div className={styles.value}>{totalSubjects}</div>
                      <div className={`${styles.iconBadge} ${styles.purple}`}>
                        <i className="ri-user-3-line" />
                      </div>
                    </div>
                    <div className={styles.cardBodyContent}>
                      <div className={styles.label}>Total Subject</div>
                      <div className={styles.subtext}>Registered Subjects</div>
                    </div>
                  </div>
                </div>

                {/* Card 3 */}
                <div>
                  <div className={styles.statItemCard}>
                    <div className={styles.cardHeaderRow}>
                      <div className={styles.value}>{completedCollection}</div>
                      <div className={`${styles.iconBadge} ${styles.green}`}>
                        <i className="ri-user-shared-line" />
                      </div>
                    </div>
                    <div className={styles.cardBodyContent}>
                      <div className={styles.label}>Completed Collection</div>
                      <div className={styles.subtext}>Sample Collections Scanned</div>
                    </div>
                  </div>
                </div>

                {/* Card 4 */}
                <div>
                  <div className={styles.statItemCard}>
                    <div className={styles.cardHeaderRow}>
                      <div className={styles.value}>{completedSeparation}</div>
                      <div className={`${styles.iconBadge} ${styles.teal}`}>
                        <i className="ri-flask-line" />
                      </div>
                    </div>
                    <div className={styles.cardBodyContent}>
                      <div className={styles.label}>Completed Seperation</div>
                      <div className={styles.subtext}>Aliquots Separated</div>
                    </div>
                  </div>
                </div>

                {/* Card 5 */}
                <div>
                  <div className={styles.statItemCard}>
                    <div className={styles.cardHeaderRow}>
                      <div className={styles.value}>{protocolDeviationLate}</div>
                      <div className={`${styles.iconBadge} ${styles.red}`}>
                        <i className="ri-error-warning-line" />
                      </div>
                    </div>
                    <div className={styles.cardBodyContent}>
                      <div className={styles.label}>Protocol Deviation</div>
                      <div className={styles.subtext}>Out of Time Window</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Middle Row: 3 Donut Chart Cards ────────────────────────────── */}
              <Row className="g-3 mb-4">
                {/* Card 1: Session State */}
                <Col xs={12} xl={4}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Session State</span>
                      <i className="ri-pie-chart-2-line" />
                    </div>
                    <div className={styles.cardBody} style={{ minHeight: "200px", display: "flex", alignItems: "center" }}>
                      {sessionStateTotal === 0 ? (
                        renderEmptyState(
                          "ri-dashboard-3-line",
                          "No Session Data",
                          "Session status breakdown will appear once accessioning sessions are created."
                        )
                      ) : (
                        <div className={styles.donutChartWrapper}>
                          <div className={styles.chartContainer}>
                            <Chart
                              options={sessionStateOptions}
                              series={sessionStateSeries}
                              type="donut"
                              width="100%"
                              height="150"
                            />
                          </div>
                          <div className={styles.donutLegend}>
                            <div className={styles.legendRow}>
                              <span className={styles.legendLabel}>
                                <span className={`${styles.bullet} ${styles.blue}`} />
                                Completed
                              </span>
                              <span className={styles.legendValue}>{sessionStateCompleted}</span>
                            </div>
                            <div className={styles.legendRow}>
                              <span className={styles.legendLabel}>
                                <span className={`${styles.bullet} ${styles.amber}`} />
                                Paused
                              </span>
                              <span className={styles.legendValue}>{sessionStatePaused}</span>
                            </div>
                            <div className={styles.legendRow}>
                              <span className={styles.legendLabel}>
                                <span className={`${styles.bullet} ${styles.green}`} />
                                Active
                              </span>
                              <span className={styles.legendValue}>{sessionStateActive}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Card 2: Sample Collection */}
                <Col xs={12} xl={4}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Sample Collection</span>
                      <i className="ri-user-shared-line" />
                    </div>
                    <div className={styles.cardBody} style={{ minHeight: "200px", display: "flex", alignItems: "center" }}>
                      {collectionTotal === 0 ? (
                        renderEmptyState(
                          "ri-user-shared-line",
                          "No Collection Data",
                          "Sample collection figures will appear once subject visits begin."
                        )
                      ) : (
                        <div className={styles.donutChartWrapper}>
                          <div className={styles.chartContainer}>
                            <Chart
                              options={sampleCollectionOptions}
                              series={sampleCollectionSeries}
                              type="donut"
                              width="100%"
                              height="150"
                            />
                          </div>
                          <div className={styles.donutLegend}>
                            <div className={styles.legendRow}>
                              <span className={styles.legendLabel}>
                                <span className={`${styles.bullet} ${styles.green}`} />
                                Completed
                              </span>
                              <span className={styles.legendValue}>{collectionCompleted}</span>
                            </div>
                            <div className={styles.legendRow}>
                              <span className={styles.legendLabel}>
                                <span className={`${styles.bullet} ${styles.red}`} />
                                Skipped
                              </span>
                              <span className={styles.legendValue}>{collectionSkipped}</span>
                            </div>
                            <div className={styles.legendRow}>
                              <span className={styles.legendLabel}>
                                <span className={`${styles.bullet} ${styles.gray}`} />
                                Pending
                              </span>
                              <span className={styles.legendValue}>{collectionPending}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Card 3: Sample Separation */}
                <Col xs={12} xl={4}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Sample Separation</span>
                      <i className="ri-flask-line" />
                    </div>
                    <div className={styles.cardBody} style={{ minHeight: "200px", display: "flex", alignItems: "center" }}>
                      {separationTotal === 0 ? (
                        renderEmptyState(
                          "ri-flask-line",
                          "No Separation Data",
                          "Aliquot separation figures will appear once sample processing begins."
                        )
                      ) : (
                        <div className={styles.donutChartWrapper}>
                          <div className={styles.chartContainer}>
                            <Chart
                              options={sampleSeparationOptions}
                              series={sampleSeparationSeries}
                              type="donut"
                              width="100%"
                              height="150"
                            />
                          </div>
                          <div className={styles.donutLegend}>
                            <div className={styles.legendRow}>
                              <span className={styles.legendLabel}>
                                <span className={`${styles.bullet} ${styles.teal}`} />
                                Completed
                              </span>
                              <span className={styles.legendValue}>{separationCompleted}</span>
                            </div>
                            <div className={styles.legendRow}>
                              <span className={styles.legendLabel}>
                                <span className={`${styles.bullet} ${styles.gray}`} />
                                Pending
                              </span>
                              <span className={styles.legendValue}>{separationPending}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* ── Bottom Row: Period & Timepoint Bar Chart vs Collection Timing Bar Chart ──────────── */}
              <Row className="g-3">
                {/* Left Panel: Period & Timepoint Bar Chart */}
                <Col xs={12} xl={6}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Period &amp; Timepoint</span>
                      <i className="ri-bar-chart-line" />
                    </div>
                    <div className={styles.cardBody} style={{ minHeight: "220px", display: "flex", alignItems: "center" }}>
                      {periodTimepointList.length === 0 ? (
                        renderEmptyState(
                          "ri-bar-chart-line",
                          "No Timepoint Data",
                          "Period timepoints breakdown will appear once sessions are configured."
                        )
                      ) : (
                        <div style={{ width: "100%" }}>
                          <Chart
                            options={periodBarOptions}
                            series={periodBarSeries}
                            type="bar"
                            width="100%"
                            height="210"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Right Panel: Collection Timing Bar Chart */}
                <Col xs={12} xl={6}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Collection Timing</span>
                      <i className="ri-time-line" />
                    </div>
                    <div className={styles.cardBody} style={{ minHeight: "220px", display: "flex", alignItems: "center" }}>
                      {earlyVal === 0 && onTimeVal === 0 && lateVal === 0 && notCollectedVal === 0 ? (
                        renderEmptyState(
                          "ri-time-line",
                          "No Timing Data",
                          "Collection timing status breakdown will appear once sample collection begins."
                        )
                      ) : (
                        <div style={{ width: "100%" }}>
                          <Chart
                            options={timingBarOptions}
                            series={timingBarSeries}
                            type="bar"
                            width="100%"
                            height="210"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </div>
  );
};

export default SessionDashboard;
